import Bugsnag from '@bugsnag/js'
import { Box, CircularProgress, Typography } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { useMutation } from 'react-query'
import AdxButton from 'components/custom/AdxButton'
import {
  getPayslipStyle,
  previewPayslipStyle,
  updatePayslipStyle,
} from 'services/api/modules/payroll/payroll-config/payslipStyleApi'

const THEME_OPTIONS = [
  {
    id: 'default-green',
    label: 'Green',
    theme_color: '#1CBA98',
    box_background_color: '#EFFAF8',
  },
  {
    id: 'red',
    label: 'Red',
    theme_color: '#B91C1C',
    box_background_color: '#FEF2F2',
  },
  {
    id: 'yellow',
    label: 'Yellow',
    theme_color: '#D4A017',
    box_background_color: '#FFFBEA',
  },
  {
    id: 'blue',
    label: 'Blue',
    theme_color: '#1E40AF',
    box_background_color: '#EFF6FF',
  },
  {
    id: 'purple',
    label: 'Purple',
    theme_color: '#800080',
    box_background_color: '#F5F3FF',
  },
  {
    id: 'orange',
    label: 'Orange',
    theme_color: '#EA580C',
    box_background_color: '#FFF7ED',
  },
]

const ThemeOptionRow = ({ theme, selected, onSelect }) => {
  return (
    <Box
      onClick={() => onSelect(theme)}
      sx={{
        border: 1,
        borderColor: selected ? theme.theme_color : 'gray-line.main',
        borderRadius: '8px',
        px: 2,
        py: 1.5,
        cursor: 'pointer',
        backgroundColor: 'white.main',
        transition: 'all 0.2s ease',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
      }}
    >
      <Box display="flex" alignItems="center" gap={1.5}>
        <Box display="flex" gap={0.75}>
          <Box
            sx={{
              width: 18,
              height: 18,
              borderRadius: '50%',
              bgcolor: theme.theme_color,
            }}
          />
          <Box
            sx={{
              width: 18,
              height: 18,
              borderRadius: '50%',
              bgcolor: theme.box_background_color,
            }}
          />
          <Box
            sx={{
              width: 18,
              height: 18,
              borderRadius: '50%',
              bgcolor: theme.theme_color,
              opacity: 0.45,
            }}
          />
        </Box>
        <Typography variant="subtitle1" fontWeight={selected ? 600 : 500}>
          {theme.label}
        </Typography>
      </Box>
      <Box
        sx={{
          width: 18,
          height: 18,
          borderRadius: '50%',
          border: 1,
          borderColor: selected ? theme.theme_color : 'gray-line.main',
          bgcolor: 'white.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {selected && (
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: theme.theme_color,
            }}
          />
        )}
      </Box>
    </Box>
  )
}

const PayslipThemeSetting = () => {
  const defaultTheme = THEME_OPTIONS[0]
  const [selectedTheme, setSelectedTheme] = useState(defaultTheme)
  const [savedTheme, setSavedTheme] = useState(defaultTheme)
  const [previewHtml, setPreviewHtml] = useState('')
  const [isPreviewLoading, setIsPreviewLoading] = useState(false)
  const [isApplyingPreview, setIsApplyingPreview] = useState(false)

  const hasUnsavedChanges = useMemo(() => {
    return (
      selectedTheme.theme_color !== savedTheme.theme_color ||
      selectedTheme.box_background_color !== savedTheme.box_background_color
    )
  }, [savedTheme, selectedTheme])

  const { mutate: savePayslipStyle, isLoading: isSaving } = useMutation(updatePayslipStyle, {
    onSuccess: (response) => {
      const payload = response?.data?.data
      const nextSavedTheme = {
        ...selectedTheme,
        theme_color: payload?.theme_color || selectedTheme.theme_color,
        box_background_color:
          payload?.box_background_color || selectedTheme.box_background_color,
      }
      setSavedTheme(nextSavedTheme)
      setSelectedTheme(nextSavedTheme)
    },
    onError: (error) => {
      Bugsnag.notify(error)
    },
  })

  const handleSave = () => {
    savePayslipStyle({
      theme_color: selectedTheme.theme_color,
      box_background_color: selectedTheme.box_background_color,
    })
  }

  const requestPreview = async (theme) => {
    setIsPreviewLoading(true)
    try {
      const response = await previewPayslipStyle({
        theme_color: theme.theme_color,
        box_background_color: theme.box_background_color,
      })
      setPreviewHtml(response.data || '')
    } catch (error) {
      Bugsnag.notify(error)
      setPreviewHtml('')
    } finally {
      setIsPreviewLoading(false)
    }
  }

  const findMatchingTheme = (style) => {
    return (
      THEME_OPTIONS.find(
        (theme) =>
          theme.theme_color?.toLowerCase() === style?.theme_color?.toLowerCase() &&
          theme.box_background_color?.toLowerCase() === style?.box_background_color?.toLowerCase()
      ) || null
    )
  }

  const handleSelectTheme = async (theme) => {
    setSelectedTheme(theme)
    setIsApplyingPreview(true)
    try {
      await requestPreview(theme)
    } catch (error) {
      Bugsnag.notify(error)
    } finally {
      setIsApplyingPreview(false)
    }
  }

  useEffect(() => {
    const loadSavedStyle = async () => {
      setIsPreviewLoading(true)
      try {
        const response = await getPayslipStyle()
        const style = response?.data?.data || response?.data
        const matchedTheme = findMatchingTheme(style) || {
          id: 'saved-custom-theme',
          label: 'Saved theme',
          theme_color: style?.theme_color || defaultTheme.theme_color,
          box_background_color:
            style?.box_background_color || defaultTheme.box_background_color,
        }

        setSelectedTheme(matchedTheme)
        setSavedTheme(matchedTheme)

        const previewResponse = await previewPayslipStyle({
          theme_color: matchedTheme.theme_color,
          box_background_color: matchedTheme.box_background_color,
        })
        setPreviewHtml(previewResponse.data || '')
      } catch (error) {
        Bugsnag.notify(error)
        setSelectedTheme(defaultTheme)
        setSavedTheme(defaultTheme)
        try {
          const previewResponse = await previewPayslipStyle({
            theme_color: defaultTheme.theme_color,
            box_background_color: defaultTheme.box_background_color,
          })
          setPreviewHtml(previewResponse.data || '')
        } catch (previewError) {
          Bugsnag.notify(previewError)
          setPreviewHtml('')
        }
      } finally {
        setIsPreviewLoading(false)
      }
    }

    loadSavedStyle()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <Box>
        <Typography variant="h4" fontWeight="bold">
          Payslip theme
        </Typography>
        <Typography color="light-gray-text.main" mb={2}>
          Description: Choose the color theme used for payslip styling. Preview updates as you
          select a theme.
        </Typography>
        <Box
          sx={{
            bgcolor: 'white.main',
            border: 1,
            borderColor: 'gray-line.main',
            borderRadius: '4.5px',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: '420px minmax(0, 1fr)' },
            }}
          >
            <Box
              sx={{
                p: 2,
                borderBottom: { xs: 1, lg: 0 },
                borderColor: 'gray-line.main',
              }}
            >
              <Typography variant="body2" color="light-gray-text.main" mb={2}>
                Changes here update the preview immediately. Your live payslip theme changes only
                after saving.
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.25,
                  maxWidth: '460px',
                }}
              >
                {THEME_OPTIONS.map((theme) => (
                  <ThemeOptionRow
                    key={theme.id}
                    theme={theme}
                    selected={selectedTheme.id === theme.id}
                    onSelect={handleSelectTheme}
                  />
                ))}
              </Box>
            </Box>

            <Box
              sx={{
                p: 2,
                pl: { xs: 2, lg: 3 },
                minHeight: '420px',
                bgcolor: 'gray-background.main',
              }}
            >
              {isPreviewLoading || isApplyingPreview ? (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  sx={{ height: '100%', minHeight: '380px' }}
                >
                  <CircularProgress color="alternate" size={48} />
                </Box>
              ) : previewHtml ? (
                <Box
                  component="iframe"
                  title="Payslip preview"
                  srcDoc={previewHtml}
                  sx={{
                    width: '100%',
                    minHeight: '420px',
                    height: { xs: '420px', lg: '560px' },
                    border: 0,
                    bgcolor: 'white.main',
                    borderRadius: '4px',
                  }}
                />
              ) : (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  sx={{ height: '100%', minHeight: '380px', p: 3 }}
                >
                  <Typography color="light-gray-text.main">
                    Unable to load preview right now.
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              borderTop: 1,
              borderColor: 'gray-line.main',
              p: 2,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                Selected: {selectedTheme.label}
              </Typography>
              <Typography variant="body2" color="light-gray-text.main">
                Theme color {selectedTheme.theme_color} | Box background{' '}
                {selectedTheme.box_background_color}
              </Typography>
            </Box>

            <Box display="flex" gap={1} flexDirection={{ xs: 'column', sm: 'row' }}>
              <AdxButton
                onClick={handleSave}
                isLoading={isSaving}
                disabled={!hasUnsavedChanges || isApplyingPreview}
              >
                Save theme
              </AdxButton>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  )
}

export default PayslipThemeSetting
